
import { useState } from "react";
import { MessageCircle, Bug, Flag } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

type FeedbackType = "bug" | "feature" | "other";

const FeedbackDialog = () => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>("feature");
  const [feedbackText, setFeedbackText] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!feedbackText.trim()) {
      toast.error("Please provide some feedback before submitting");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Save feedback to Supabase
      const { error } = await supabase.from("feedback").insert({
        user_id: user?.id || null,
        type: feedbackType,
        message: feedbackText,
        email: email || null,
      });
      
      if (error) throw error;
      
      toast.success("Thanks for your feedback!");
      
      // Reset form and close dialog
      setFeedbackText("");
      setEmail("");
      setIsOpen(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Error submitting feedback. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          size="sm" 
          variant="outline" 
          className="fixed bottom-6 right-6 gap-2 shadow-md rounded-full px-4 py-2 z-50 bg-primary text-primary-foreground border-primary hover:bg-primary/90 hover:text-primary-foreground"
        >
          <MessageCircle size={18} />
          <span>Feedback</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Share your feedback</DialogTitle>
            <DialogDescription>
              Help us improve by sharing your ideas, bug reports, or feature requests.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="flex flex-wrap gap-2">
              <Button 
                type="button"
                size="sm"
                variant={feedbackType === "feature" ? "default" : "outline"}
                onClick={() => setFeedbackType("feature")}
                className="flex items-center gap-1"
              >
                <Flag size={16} />
                Feature request
              </Button>
              <Button 
                type="button"
                size="sm"
                variant={feedbackType === "bug" ? "default" : "outline"}
                onClick={() => setFeedbackType("bug")}
                className="flex items-center gap-1"
              >
                <Bug size={16} />
                Report a bug
              </Button>
              <Button 
                type="button"
                size="sm"
                variant={feedbackType === "other" ? "default" : "outline"}
                onClick={() => setFeedbackType("other")}
              >
                Other
              </Button>
            </div>
            
            <div className="space-y-2">
              <Textarea
                placeholder={
                  feedbackType === "feature" 
                    ? "I would like to see..." 
                    : feedbackType === "bug" 
                      ? "I found an issue with..." 
                      : "I want to share..."
                }
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                className="min-h-[120px]"
                required
              />
            </div>
            
            <div className="space-y-2">
              <input
                type="email"
                placeholder="Your email (optional)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              <p className="text-xs text-muted-foreground">
                We'll only use your email to follow up on your feedback if needed.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit feedback"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
